import { type CustomArticle, HELP_CATEGORIES, type HelpArticleMeta, type HelpRole, helpVisibleTo } from '@hoopoe/shared';
import GithubSlugger from 'github-slugger';
import { parse as parseYaml } from 'yaml';

export interface Heading {
  depth: 2 | 3;
  text: string;
  id: string;
}
export interface Article extends HelpArticleMeta {
  body: string;
  headings: Heading[];
  /** Set on admin-written articles (PLAN §12.6 custom articles). */
  custom?: { updatedAt: number; updatedByName: string | null };
}
export interface GlossaryEntry {
  term: string;
  definition: string;
  see?: string;
  id: string;
}

/** Splits `---\nyaml\n---\nbody`. */
export function parseArticle(slug: string, source: string): Article {
  const m = source.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) throw new Error(`docs/help/${slug}.md: missing frontmatter`);
  const meta = parseYaml(m[1] ?? '') as Omit<HelpArticleMeta, 'slug'>;
  if (!meta.title || !meta.category) throw new Error(`docs/help/${slug}.md: title and category are required`);
  if (!HELP_CATEGORIES.some(c => c.key === meta.category)) throw new Error(`docs/help/${slug}.md: unknown category ${meta.category}`);
  const body = m[2] ?? '';
  return { ...meta, slug, body, headings: extractHeadings(body) };
}

/** Same slugs as rehype-slug (both use github-slugger), so the outline links match the rendered ids. */
export function extractHeadings(body: string): Heading[] {
  const slugger = new GithubSlugger();
  const out: Heading[] = [];
  let fence = false;
  for (const line of body.split('\n')) {
    if (line.startsWith('```')) fence = !fence;
    const h = !fence && line.match(/^(#{1,6})\s+(.+?)\s*#*$/);
    if (!h) continue;
    const text = (h[2] ?? '').replace(/[*_`]/g, '');
    const id = slugger.slug(text);
    const depth = h[1]?.length;
    if (depth === 2 || depth === 3) out.push({ depth, text, id });
  }
  return out;
}

const files = import.meta.glob<string>('../../../../../docs/help/*.md', { query: '?raw', import: 'default', eager: true });
const glossaryRaw = import.meta.glob<string>('../../../../../docs/help/glossary.yaml', { query: '?raw', import: 'default', eager: true });

export const ARTICLES: Article[] = Object.entries(files)
  .map(([path, src]) => parseArticle(path.split('/').pop()?.replace(/\.md$/, '') ?? '', src))
  .sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || a.title.localeCompare(b.title));

/**
 * Screenshots in how-to guides (docs/help/images, generated by `pnpm help:screenshots`). Articles reference them as
 * `![alt](images/name.png)`; only these files are shown, so an article can't load images from elsewhere.
 */
const imageFiles = import.meta.glob<string>('../../../../../docs/help/images/*.png', { query: '?url', import: 'default', eager: true });
export const HELP_IMAGES: Record<string, string> = Object.fromEntries(
  Object.entries(imageFiles).map(([path, url]) => [path.split('/').pop() ?? '', url]),
);
export const helpImageName = (src: string) => src.match(/^(?:\.\/)?images\/([a-z0-9-]+\.png)$/)?.[1];
export const helpImageUrl = (src: string) => {
  const name = helpImageName(src);
  return name ? HELP_IMAGES[name] : undefined;
};

const glossarySlugger = new GithubSlugger();
export const GLOSSARY: GlossaryEntry[] = ((parseYaml(Object.values(glossaryRaw)[0] ?? '[]') as Omit<GlossaryEntry, 'id'>[]) ?? [])
  .map(g => ({ ...g, id: glossarySlugger.slug(g.term) }))
  .sort((a, b) => a.term.localeCompare(b.term));

export const GLOSSARY_SLUG = 'glossary';

/** An admin-written article from the API, shaped like a built-in one. */
export const fromCustom = (a: CustomArticle): Article => ({
  slug: a.slug,
  title: a.title,
  category: a.category,
  type: a.type ?? undefined,
  permission: a.permission ?? undefined,
  tags: a.tags,
  related: a.related,
  summary: a.summary ?? undefined,
  order: 50,
  body: a.body,
  headings: extractHeadings(a.body),
  custom: { updatedAt: a.updatedAt, updatedByName: a.updatedByName ?? null },
});

const BUILT_IN = new Set(ARTICLES.map(a => a.slug));
/** True when a built-in article (or the glossary) owns the slug: it wins, and a custom article there is hidden. */
export const isBuiltInSlug = (slug: string) => slug === GLOSSARY_SLUG || BUILT_IN.has(slug);

/** Built-in articles plus the custom ones (`extra`) whose slug no built-in article uses. */
export const allArticles = (extra: Article[] = []) =>
  extra.length
    ? [...ARTICLES, ...extra.filter(a => !isBuiltInSlug(a.slug))].sort(
        (a, b) => (a.order ?? 99) - (b.order ?? 99) || a.title.localeCompare(b.title),
      )
    : ARTICLES;

export const articleBySlug = (slug: string, extra: Article[] = []) =>
  ARTICLES.find(a => a.slug === slug) ?? extra.find(a => a.slug === slug);

export const articlesFor = (role: HelpRole, extra: Article[] = []) => allArticles(extra).filter(a => helpVisibleTo(a, role));

/** Topic tree: categories in fixed order, visible articles only; the glossary sits under Core concepts. */
export function topicTree(role: HelpRole, extra: Article[] = []) {
  const visible = articlesFor(role, extra);
  return HELP_CATEGORIES.map(c => ({
    ...c,
    articles: [
      ...visible.filter(a => a.category === c.key).map(a => ({ slug: a.slug, title: a.title })),
      ...(c.key === 'core-concepts' ? [{ slug: GLOSSARY_SLUG, title: 'Glossary' }] : []),
    ],
  })).filter(c => c.articles.length > 0);
}
